(() => {
  if (window.FoxtrotCart) return;

  const root = document.querySelector('[data-fg-cart-root]');
  if (!root) return;

  const panel = root.querySelector('.fg-cart-drawer__panel');
  const backdrop = root.querySelector('.fg-cart-drawer__backdrop');
  const toast = root.querySelector('[data-fg-cart-toast]');
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let cart = null;
  let busy = false;
  let refreshPromise = null;
  const idleWaiters = new Set();
  let opener = null;
  let toastTimer = 0;
  let closeTimer = 0;
  let focusTimer = 0;
  let promotionConfig = null;
  let promotionError = '';
  let suppressAutomaticUnlock = false;
  let verifiedUnlockOwner = false;
  const promotion = window.FoxtrotKitCartPromotion;
  try {
    if (root.dataset.kitPromotion) {
      if (!promotion) throw new Error('The Kit Builder cart promotion could not load.');
      const settings = JSON.parse(root.dataset.kitPromotion);
      promotion.validatePromotionConfig(settings);
      promotionConfig = settings;
    }
  } catch (error) {
    console.error('Foxtrot cart promotion configuration failed.', error);
    promotionError = error.message;
  }

  const escapeHTML = (value) => {
    const element = document.createElement('span');
    element.textContent = String(value ?? '');
    return element.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  };

  const formatMoney = (cents) => {
    const currency = cart?.currency || window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      console.error('Cart currency could not be formatted.', error);
      return `${currency} ${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const validateCart = (value) => {
    if (!value || !Array.isArray(value.items) || !Number.isFinite(Number(value.total_price))) {
      throw new Error('Shopify returned an unexpected cart response.');
    }
    return value;
  };

  const requestJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
    });
    let data;
    try {
      data = await response.json();
    } catch (error) {
      throw new Error(`Shopify returned an unreadable response (${response.status}).`);
    }
    if (!response.ok) {
      throw new Error(data.description || data.message || `Cart request failed (${response.status}).`);
    }
    return data;
  };

  const readCart = async () => validateCart(await requestJSON(root.dataset.cartUrl));

  const reconcileCart = async (nextCart, suppressAdd = suppressAutomaticUnlock, verifiedOwnership) => {
    if (!promotionConfig) {
      if (promotionError) {
        renderCart(validateCart(nextCart));
        throw new Error(promotionError);
      }
      return validateCart(nextCart);
    }
    try {
      const alreadyUnlocked = verifiedOwnership ?? await promotion.hasVerifiedCustomerUnlock(promotionConfig);
      verifiedUnlockOwner = alreadyUnlocked;
      const settled = await promotion.reconcilePromotion(validateCart(nextCart), promotionConfig, {
        read: readCart,
        add: async (variantId, properties) => {
          await requestJSON(root.dataset.cartAddUrl, {
            method: 'POST', body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties }] }),
          });
        },
        change: async (key, quantity) => validateCart(await requestJSON(root.dataset.cartChangeUrl, {
          method: 'POST', body: JSON.stringify({ id: key, quantity }),
        })),
      }, suppressAdd, alreadyUnlocked);
      promotionError = '';
      return settled;
    } catch (error) {
      promotionError = error.message || 'The Kit Builder promotion could not be verified.';
      if (error instanceof promotion.PromotionReconciliationError) renderCart(error.cart);
      throw error;
    }
  };

  const itemTitle = (item) => item.product_title || item.title || 'Product';
  const publicUnlockAvailable = () => root.dataset.kitUnlockPurchasable === 'true' &&
    /^[1-9]\d*$/.test(root.dataset.kitUnlockProductId || '') &&
    /^[1-9]\d*$/.test(root.dataset.kitUnlockVariantId || '');
  const unlockVariantId = () => promotionConfig?.unlockVariantId.split('/').pop() ||
    (publicUnlockAvailable() ? root.dataset.kitUnlockVariantId : null);
  const isUnlockLine = (item) => promotionConfig
    ? promotion.isUnlockLine(item, promotionConfig)
    : publicUnlockAvailable() && String(item.product_id) === root.dataset.kitUnlockProductId &&
      String(item.variant_id) === root.dataset.kitUnlockVariantId;

  const renderKitOffer = () => {
    const unlock = cart.items.find(isUnlockLine);
    const confirmedFree = unlock?.final_line_price === 0 && !promotionError;
    document.querySelectorAll('[data-fg-kit-cart-offer]').forEach(offer => {
      offer.hidden = Boolean(unlock);
      offer.classList.toggle('is-complimentary', Boolean(confirmedFree));
      const message = offer.querySelector('[data-fg-kit-cart-offer-message]');
      const action = offer.querySelector('[data-fg-kit-cart-offer-action]');
      const status = offer.querySelector('[data-fg-kit-cart-offer-status]');
      const artwork = offer.querySelector('[data-fg-kit-cart-art]');
      const artworkMatchesOffer = !promotionConfig || (
        promotionConfig.currencyCode === 'USD' && promotionConfig.complimentaryThresholdCents === 5000
      );
      if (artwork) artwork.hidden = !artworkMatchesOffer || verifiedUnlockOwner;
      if (status) status.textContent = verifiedUnlockOwner
        ? 'Already unlocked'
        : 'Optional add-on · Not in your cart';
      if (!message || !action) return;
      action.replaceChildren();
      const link = document.createElement('a');
      link.href = verifiedUnlockOwner ? '/pages/my-kit' : root.dataset.kitUnlockUrl || '/pages/my-kit';
      link.textContent = verifiedUnlockOwner ? 'Open My Kits' : 'Visit Kit Builder';
      if (verifiedUnlockOwner) {
        message.textContent = 'Already unlocked — your account has access.';
      } else if (confirmedFree) {
        message.textContent = 'FREE with your qualifying merchandise purchase — included in this cart.';
        const badge = document.createElement('strong');
        badge.className = 'fg-kit-cart-offer__badge'; badge.textContent = 'FREE';
        action.append(badge);
      } else if (unlock) {
        message.textContent = `Kit Builder unlock in your cart: ${formatMoney(unlock.final_line_price)}.`;
      } else {
        const price = Number(root.dataset.kitUnlockPrice);
        const canPurchase = publicUnlockAvailable() && !promotionError && (!promotionConfig || (
          `gid://shopify/Product/${root.dataset.kitUnlockProductId}` === promotionConfig.unlockProductId &&
          `gid://shopify/ProductVariant/${root.dataset.kitUnlockVariantId}` === promotionConfig.unlockVariantId)) &&
          Number.isSafeInteger(price) && price > 0;
        const active = promotionConfig?.enabled && promotionConfig.discountReady === true &&
          cart.currency === promotionConfig.currencyCode;
        message.textContent = `Build and store your kits - FREE when your order is over ${formatMoney(promotionConfig?.complimentaryThresholdCents ?? 5000).replace(/\.00$/, '')}!`;
        if (!active && status) status.textContent = 'Optional add-on · Not in your cart · Free offer setup pending';
        if (canPurchase) {
          const priceLabel = document.createElement('strong');
          priceLabel.className = 'fg-kit-cart-offer__price';
          priceLabel.textContent = formatMoney(price);
          action.append(priceLabel);
          const form = document.createElement('form');
          form.method = 'post'; form.action = root.dataset.cartAddUrl;
          const id = document.createElement('input');
          id.type = 'hidden'; id.name = 'id'; id.value = unlockVariantId();
          const button = document.createElement('button');
          button.type = 'submit'; button.dataset.fgKitOfferAdd = '';
          button.textContent = 'Add to Cart'; button.disabled = busy;
          form.append(id, button); action.append(form);
        } else {
          const unavailable = document.createElement('button');
          unavailable.type = 'button'; unavailable.disabled = true;
          unavailable.textContent = 'Add to Cart';
          unavailable.title = 'Kit Builder purchasing is not available yet.';
          action.append(unavailable);
        }
      }
      action.append(link);
    });
  };

  const renderLine = (item, compact) => {
    const title = itemTitle(item);
    const key = escapeHTML(item.key);
    const url = escapeHTML(item.url || '#');
    const imageUrl = isUnlockLine(item) && (!promotionConfig || (
      promotionConfig.currencyCode === 'USD' && promotionConfig.complimentaryThresholdCents === 5000))
      ? root.dataset.kitUnlockArtUrl || item.image : item.image;
    const image = imageUrl
      ? `<img src="${escapeHTML(imageUrl)}" alt="" loading="lazy" width="120" height="120">`
      : '<span class="fg-cart-line__placeholder" aria-hidden="true"></span>';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="fg-cart-line__variant">${escapeHTML(item.variant_title)}</p>`
      : '';
    const stock = item.variant_available === false
      ? '<p class="fg-cart-line__stock fg-cart-line__stock--limited">Availability confirmed at checkout</p>'
      : '<p class="fg-cart-line__stock"><span aria-hidden="true"></span>In Stock</p>';
    const quantity = Number(item.quantity || 0);
    const automaticUnlock = promotionConfig && promotion.isAutomaticUnlock(item, promotionConfig);
    const unlockItem = isUnlockLine(item);
    const complimentaryUnlock = unlockItem && item.final_line_price === 0 && !promotionError;

    return `<article class="fg-cart-line${compact ? ' fg-cart-line--compact' : ''}${complimentaryUnlock ? ' fg-cart-line--complimentary' : ''}" data-fg-cart-line data-fg-key="${key}">
      <a class="fg-cart-line__image" href="${url}" tabindex="-1" aria-hidden="true">${image}</a>
      <div class="fg-cart-line__details">
        <a class="fg-cart-line__title" href="${url}">${escapeHTML(title)}</a>
        ${variant}${complimentaryUnlock ? '<p class="fg-cart-line__benefit">FREE — included in your cart</p>' : automaticUnlock ? '<p class="fg-cart-line__variant">Kit Builder promotion pending verification</p>' : stock}
        <p class="fg-cart-line__unit">${formatMoney(item.final_price ?? item.price)}</p>
        <button class="fg-cart-line__remove" type="button" data-fg-cart-action="remove" aria-label="Remove ${escapeHTML(title)}">Remove</button>
      </div>
      <div class="fg-cart-line__quantity" role="group" aria-label="Quantity for ${escapeHTML(title)}">
        <button type="button" data-fg-cart-action="decrease" aria-label="Decrease quantity of ${escapeHTML(title)}"${quantity <= 1 ? ' disabled' : ''}>&minus;</button>
        <span data-fg-cart-quantity aria-live="polite">${quantity}</span>
        <button type="button" data-fg-cart-action="increase" aria-label="Increase quantity of ${escapeHTML(title)}"${unlockItem ? ' disabled' : ''}>+</button>
      </div>
      <strong class="fg-cart-line__total" data-fg-cart-line-total>${complimentaryUnlock ? '<span class="fg-kit-cart-offer__badge">FREE</span>' : formatMoney(item.final_line_price)}</strong>
    </article>`;
  };

  const updateBusyControls = () => {
    document.querySelectorAll('[data-fg-kit-offer-add]').forEach(button => { button.disabled = busy; });
    document.querySelectorAll('[data-fg-cart-action], [data-fg-cart-note]').forEach((control) => {
      const line = control.closest('[data-fg-cart-line]');
      const item = line && cart?.items.find(item => String(item.key) === line.dataset.fgKey);
      const quantityAction = control.dataset.fgCartAction;
      control.disabled = busy || Boolean(item && (
        quantityAction === 'decrease' && Number(item.quantity) <= 1 ||
        quantityAction === 'increase' && isUnlockLine(item)
      ));
    });
    document.querySelectorAll('[data-fg-cart-checkout]').forEach((checkout) => {
      const blocked = busy || Boolean(promotionError) || !cart?.items.length;
      checkout.setAttribute('aria-disabled', String(blocked));
      checkout.tabIndex = blocked ? -1 : 0;
      checkout.classList.toggle('is-disabled', blocked);
    });
  };

  const renderCart = (nextCart, { preserveLines = false, notify = true } = {}) => {
    cart = validateCart(nextCart);
    const count = Number(cart.item_count ?? cart.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0));
    const countLabel = root.dataset.cartCountLabel || 'Cart (COUNT)';

    document.querySelectorAll('[data-fg-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
    document.querySelectorAll('[data-fg-cart-open]').forEach((button) => {
      button.setAttribute('aria-label', countLabel.replace('COUNT', String(count)));
    });
    document.querySelectorAll('[data-fg-cart-drawer-count]').forEach((label) => {
      label.textContent = `(${count})`;
    });
    if (!preserveLines) {
      document.querySelectorAll('[data-fg-cart-lines]').forEach((list) => {
        const compact = list.classList.contains('fg-cart-drawer__lines');
        const items = [...cart.items];
        items.sort((a, b) => Number(isUnlockLine(b)) - Number(isUnlockLine(a)));
        list.innerHTML = items.map((item) => renderLine(item, compact)).join('');
      });
    }
    document.querySelectorAll('[data-fg-cart-empty]').forEach((empty) => {
      empty.hidden = count !== 0;
    });
    document.querySelectorAll('.fg-cart__table-head').forEach((heading) => {
      heading.hidden = count === 0;
    });
    document.querySelectorAll('[data-fg-cart-summary], [data-fg-cart-instructions]').forEach((element) => {
      element.hidden = count === 0;
    });
    document.querySelectorAll('[data-fg-cart-subtotal]').forEach((element) => {
      element.textContent = formatMoney(cart.total_price);
    });
    document.querySelectorAll('[data-fg-cart-total], [data-fg-cart-checkout-total]').forEach((element) => {
      element.textContent = formatMoney(cart.total_price);
    });
    document.querySelectorAll('[data-fg-cart-checkout]').forEach((checkout) => {
      const empty = count === 0;
      checkout.href = empty ? root.dataset.cartPageUrl : root.dataset.checkoutUrl;
      checkout.setAttribute('aria-disabled', String(empty));
      checkout.tabIndex = empty ? -1 : 0;
      checkout.classList.toggle('is-disabled', empty);
    });
    const note = document.querySelector('[data-fg-cart-note]');
    if (note && document.activeElement !== note && note.value !== (cart.note || '')) {
      note.value = cart.note || '';
    }
    renderKitOffer();
    updateBusyControls();
    if (notify) document.dispatchEvent(new CustomEvent('foxtrot:cart:updated', { detail: structuredClone(cart) }));
  };

  const showToast = (message, isError = false, imageURL = '') => {
    window.clearTimeout(toastTimer);
    toast.replaceChildren();
    if (imageURL && !isError) {
      const image = document.createElement('img');
      image.src = imageURL;
      image.alt = '';
      image.width = 44;
      image.height = 44;
      toast.append(image);
    }
    const text = document.createElement('span');
    text.textContent = message;
    toast.append(text);
    toast.classList.toggle('is-error', isError);
    toast.classList.add('is-visible');
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  const showCartError = (message) => {
    document.querySelectorAll('[data-fg-cart-error]').forEach((element) => {
      element.textContent = message;
      element.hidden = false;
    });
    showToast(message, true);
  };

  const clearCartErrors = () => {
    document.querySelectorAll('[data-fg-cart-error]').forEach((element) => {
      element.textContent = '';
      element.hidden = true;
    });
  };

  const setBusy = (value) => {
    busy = value;
    root.setAttribute('aria-busy', String(value));
    document.querySelectorAll('[data-fg-cart-page]').forEach(element => element.setAttribute('aria-busy', String(value)));
    document.querySelectorAll('[data-fg-cart-loading]').forEach(element => {
      element.hidden = !value;
      element.textContent = value ? 'Updating your cart...' : '';
    });
    updateBusyControls();
    if (!value) {
      if (root.dataset.open === 'true' && !panel.contains(document.activeElement)) {
        root.querySelector('.fg-cart-drawer__close')?.focus();
      }
      idleWaiters.forEach(resolve => resolve());
      idleWaiters.clear();
    }
  };

  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const syncCart = () => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      if (busy) await new Promise(resolve => idleWaiters.add(resolve));
      setBusy(true);
      try {
        clearCartErrors();
        renderCart(await reconcileCart(await readCart()));
      } catch (error) {
        try {
          await recoverCart();
        } catch (recoveryError) {
          console.error('Shopify cart could not be recovered after a refresh failure.', recoveryError);
        }
        throw error;
      } finally { setBusy(false); }
    })().finally(() => { refreshPromise = null; });
    return refreshPromise;
  };
  const recoverCart = async () => {
    // Never repeat a possibly committed add during recovery.
    renderCart(await reconcileCart(await readCart(), true));
  };

  const openDrawer = (trigger) => {
    window.clearTimeout(closeTimer);
    window.clearTimeout(focusTimer);
    opener = trigger || document.activeElement;
    root.dataset.open = 'true';
    panel.style.visibility = 'visible';
    backdrop.style.visibility = 'visible';
    panel.setAttribute('aria-hidden', 'false');
    backdrop.setAttribute('aria-hidden', 'false');
    document.body.classList.add('fg-cart-open');
    document.querySelectorAll('[data-fg-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    root.querySelector('.fg-cart-drawer__close')?.focus();
    focusTimer = window.setTimeout(() => {
      if (root.dataset.open === 'true') root.querySelector('.fg-cart-drawer__close')?.focus();
    }, reducedMotion.matches ? 0 : 50);
    void syncCart().catch(error => {
      console.error('Shopify cart could not be reconciled when opened.', error);
      showCartError(error.message || 'Your cart could not be updated. Close and reopen it to try again.');
    });
  };

  const closeDrawer = () => {
    if (root.dataset.open !== 'true') return;
    window.clearTimeout(focusTimer);
    root.dataset.open = 'false';
    document.body.classList.remove('fg-cart-open');
    backdrop.setAttribute('aria-hidden', 'true');
    panel.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-fg-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    closeTimer = window.setTimeout(() => {
      panel.style.visibility = 'hidden';
      backdrop.style.visibility = 'hidden';
      if (opener?.isConnected) opener.focus();
    }, reducedMotion.matches ? 0 : 260);
  };

  const findCartItem = (key) => cart?.items.find((item) => String(item.key) === String(key));

  const optimisticallyChange = (item, quantity) => {
    const updatedItems = cart.items.map((current) => {
      if (current.key !== item.key) return current;
      const unitPrice = Number(current.final_price ?? current.price ?? 0);
      return { ...current, quantity, final_line_price: unitPrice * quantity };
    }).filter((current) => current.quantity > 0);
    const total = updatedItems.reduce((sum, current) => sum + Number(current.final_line_price || 0), 0);
    return {
      ...cart,
      items: updatedItems,
      item_count: updatedItems.reduce((sum, current) => sum + Number(current.quantity || 0), 0),
      total_price: total,
    };
  };

  const changeQuantity = async (key, quantity, remove = false) => {
    if (busy || !cart) return;
    const item = findCartItem(key);
    if (!item) {
      showCartError('That item is no longer in your cart. Please try again.');
      try {
        await syncCart();
      } catch (error) {
        console.error('Shopify cart could not be refreshed after an item lookup failure.', error);
      }
      return;
    }
    const previousCart = cart;
    clearCartErrors();
    if (remove) {
      document.querySelectorAll('[data-fg-cart-line]').forEach((line) => {
        if (line.dataset.fgKey === String(key)) line.classList.add('is-removing');
      });
      renderCart(optimisticallyChange(item, 0), { preserveLines: true, notify: false });
    } else {
      renderCart(optimisticallyChange(item, quantity), { notify: false });
    }
    setBusy(true);
    const animation = remove && !reducedMotion.matches ? delay(240) : Promise.resolve();
    try {
      const request = requestJSON(root.dataset.cartChangeUrl, {
        method: 'POST',
        body: JSON.stringify({ id: item.key, quantity }),
      });
      const [updatedCart] = await Promise.all([request, animation]);
      if (remove && promotionConfig && promotion.isAutomaticUnlock(item, promotionConfig)) suppressAutomaticUnlock = true;
      renderCart(await reconcileCart(updatedCart));
      showToast(remove ? `${itemTitle(item)} removed from your cart.` : `${itemTitle(item)} quantity updated.`);
    } catch (error) {
      console.error('Shopify cart quantity update failed.', error);
      renderCart(promotion && error instanceof promotion.PromotionReconciliationError ? error.cart : previousCart);
      showCartError(error.message || 'Your cart could not be updated. Please try again.');
      try {
        await recoverCart();
      } catch (syncError) {
        console.error('Shopify cart could not be refreshed after an update failure.', syncError);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async (form, submitter) => {
    if (busy) {
      showToast('Please wait for the current cart update to finish.', true);
      return;
    }
    if (!form.reportValidity()) return;
    const button = submitter || form.querySelector('[type="submit"][name="add"], [data-fg-add]');
    const label = button?.querySelector('[data-fg-add-label]');
    const initialLabel = label?.textContent || '';
    const initialDisabled = Boolean(button?.disabled);
    const restoreButton = () => {
      if (!button) return;
      button.classList.remove('is-adding', 'is-added');
      button.removeAttribute('aria-busy');
      button.disabled = initialDisabled;
      if (label) label.textContent = initialLabel;
    };
    const productCard = form.closest('.fg-product-card, .fg-product');
    const productName = productCard?.querySelector('h1, h2, h3')?.textContent.trim() || '';
    clearCartErrors();
    setBusy(true);
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-adding');
      if (label) label.textContent = 'Adding...';
    }

    try {
      const formData = new FormData(form);
      if (unlockVariantId() && String(formData.get('id')) === unlockVariantId()) {
        const owned = promotionConfig ? await promotion.hasVerifiedCustomerUnlock(promotionConfig) : false;
        renderCart(await reconcileCart(await readCart(), true, owned));
        if (owned || cart.items.some(isUnlockLine)) {
          showToast(owned ? 'Your account already owns the Kit Builder unlock.' : 'The Kit Builder unlock is already in your cart.');
          restoreButton();
          return;
        }
        formData.set('quantity', '1');
        formData.delete(`properties[${promotion?.AUTOMATIC_UNLOCK_PROPERTY || '_foxtrot_kit_unlock'}]`);
      }
      const added = await requestJSON(root.dataset.cartAddUrl, {
        method: 'POST',
        body: formData,
      });
      let updatedCart;
      try {
        updatedCart = await readCart();
      } catch (error) {
        throw new Error('The item was added, but your cart could not be refreshed. Please reload to confirm.');
      }
      renderCart(await reconcileCart(updatedCart));
      const name = added.product_title || added.title || productName || 'Item';
      const addedImage = added.image || added.featured_image?.url || '';
      showToast(`${name} added to your cart.`, false, addedImage);
      root.dispatchEvent(new Event('cart:added'));
      if (button) {
        button.classList.remove('is-adding');
        button.classList.add('is-added');
        if (label) label.textContent = 'Added to cart';
        window.setTimeout(restoreButton, 1250);
      }
    } catch (error) {
      console.error('Shopify add-to-cart request failed.', error);
      showCartError(error.message || 'This item could not be added. Please try again.');
      try {
        await recoverCart();
      } catch (syncError) {
        console.error('Shopify cart could not be refreshed after an add failure.', syncError);
      }
      restoreButton();
    } finally {
      setBusy(false);
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || !/\/cart\/add(?:\.js)?(?:$|[?#])/.test(new URL(form.action, location.href).pathname + new URL(form.action, location.href).search)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void handleAdd(form, event.submitter);
  }, true);

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-fg-cart-open]');
    if (openButton) {
      event.preventDefault();
      openDrawer(openButton);
      return;
    }
    const closeButton = event.target.closest('[data-fg-cart-close]');
    if (closeButton) {
      event.preventDefault();
      closeDrawer();
      return;
    }
    const checkout = event.target.closest('[data-fg-cart-checkout]');
    if (checkout?.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }
    if (checkout && promotionConfig) {
      event.preventDefault();
      void (async () => {
        if (busy) return;
        setBusy(true);
        try {
          clearCartErrors();
          renderCart(await reconcileCart(await readCart()));
          if (cart.items.length && !promotionError) location.assign(root.dataset.checkoutUrl);
        } catch (error) {
          console.error('Foxtrot checkout cart verification failed.', error);
          showCartError(error.message || 'Your cart could not be verified before checkout.');
        } finally { setBusy(false); }
      })();
      return;
    }
    const action = event.target.closest('[data-fg-cart-action]');
    if (!action) return;
    const line = action.closest('[data-fg-cart-line]');
    if (!line) return;
    event.preventDefault();
    const item = findCartItem(line.dataset.fgKey);
    if (!item) return;
    const type = action.dataset.fgCartAction;
    if (type === 'remove') void changeQuantity(item.key, 0, true);
    else if (type === 'increase' && !isUnlockLine(item)) void changeQuantity(item.key, Number(item.quantity) + 1);
    else if (type === 'decrease') void changeQuantity(item.key, Math.max(1, Number(item.quantity) - 1));
  });

  document.addEventListener('keydown', (event) => {
    if (root.dataset.open !== 'true') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(panel.querySelectorAll(focusableSelector))
      .filter((element) => element.getClientRects().length && element.getAttribute('aria-hidden') !== 'true');
    if (!focusable.length) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  });

  root.addEventListener('click', (event) => {
    if (event.target === root.querySelector('.fg-cart-drawer__backdrop')) closeDrawer();
  });

  document.addEventListener('change', async (event) => {
    const note = event.target.closest('[data-fg-cart-note]');
    if (!note || busy) return;
    setBusy(true);
    const status = document.querySelector('[data-fg-cart-note-status]');
    if (status) status.textContent = 'Saving…';
    try {
      const updatedCart = await requestJSON(root.dataset.cartUpdateUrl, {
        method: 'POST',
        body: JSON.stringify({ note: note.value }),
      });
      renderCart(await reconcileCart(updatedCart));
      if (status) status.textContent = 'Instructions saved.';
    } catch (error) {
      console.error('Shopify cart instructions could not be saved.', error);
      if (status) status.textContent = 'Could not save instructions. Please try again.';
      showCartError(error.message || 'Your order instructions could not be saved.');
      try {
        await recoverCart();
      } catch (syncError) {
        console.error('Shopify cart could not be refreshed after a note failure.', syncError);
      }
    } finally {
      setBusy(false);
    }
  });

  document.querySelectorAll('[data-fg-cart-open]').forEach((button) => {
    button.addEventListener('animationend', (event) => {
      if (event.animationName === 'fg-cart-bounce') button.classList.remove('is-bouncing');
    });
  });

  root.addEventListener('cart:added', () => {
    document.querySelectorAll('[data-fg-cart-open]').forEach((button) => {
      button.classList.remove('is-bouncing');
      void button.offsetWidth;
      button.classList.add('is-bouncing');
    });
  });

  window.FoxtrotCart = {
    get cart() { return cart; },
    refresh: syncCart,
    open: openDrawer,
    close: closeDrawer,
  };

  const refreshVisibleCart = () => {
    if (document.hidden || (root.dataset.open !== 'true' && !document.querySelector('[data-fg-cart-page]'))) return;
    void syncCart().catch(error => {
      console.error('Shopify cart could not be refreshed after a pricing change.', error);
      showCartError(error.message || 'Your cart could not be updated.');
    });
  };
  document.addEventListener('visibilitychange', refreshVisibleCart);
  const requestCartRefresh = () => {
    void syncCart().catch(error => {
      console.error('Shopify cart could not be refreshed after an external update.', error);
      showCartError(error.message || 'Your cart could not be updated.');
    });
  };
  document.addEventListener('foxtrot:cart:refresh', requestCartRefresh);
  document.addEventListener('foxtrot:account:identity', () => {
    if (promotionConfig) requestCartRefresh();
  });

  window.addEventListener('pageshow', event => {
    if (event.persisted) void syncCart().catch(error => {
      console.error('Shopify cart could not be refreshed after returning to the page.', error);
      showCartError(error.message || 'Your cart could not be updated.');
    });
  });

  void syncCart().catch((error) => {
    console.error('Shopify cart could not be loaded.', error);
    showCartError(error.message || 'Your cart could not be loaded. Please reopen the cart to try again.');
  });
})();
